import React, { Suspense } from "react";
import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import { NewOrderPage } from "../features/new-order";
import { SavedOrdersPage } from "../features/saved-orders";
import { ProceduresPage } from "../features/procedures";
import { DownloadPage } from "../features/download";
import { OpmePage } from "../features/opme";
import { SettingsPage } from "../features/settings";
import { PatientDataPage } from "../features/patient-data";
import { SurgicalIndicationPage } from "../features/surgical-indication";
import { AppealsPage } from "../features/appeals";
import { OnboardingPage, SignupRedirect } from "../features/onboarding";
import { adminChildRoutes } from "../Pages/Admin/adminSections";
import { LoadingScreen } from "./LoadingScreen";
import { RootLayout } from "./RootLayout";
import { ErrorScreen } from "./ErrorScreen";
import { ProtectedRoute } from "./ProtectedRoute";
import { AdminRoute } from "./AdminRoute";
import { AuthCallbackPage } from "./AuthCallbackPage";
import { PATHS, RENAMED_PATHS } from "./paths";

// Telas que a maioria nunca abre (pagamento, convites, admin) vêm num arquivo à parte, baixado só ao abrir.
const AdminLayout = React.lazy(() => import("../Pages/Admin/AdminLayout"));
const billing = (name: "PlansPage" | "CheckoutRedirect" | "CheckoutConfirmingPage" | "PaymentInvitePage" | "AccessInvitePage") => () =>
  import("../features/billing").then((m) => ({ Component: m[name] }));

/** Endereço antigo → novo, mantendo query e hash (ex.: retorno do Google em ?email_conectado=1). */
const RenamedRedirect: React.FC<{ to: string }> = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
};

export const router = createBrowserRouter([
  {
    // Raiz sem path: banner offline, relogin, UpgradeModal global e tela de erro valem para todas as rotas.
    element: <RootLayout />,
    errorElement: <ErrorScreen />,
    children: [
      // Root always enters the auth flow; AuthCallbackPage handles the returnTo redirect
      { path: PATHS.root, element: <Navigate to={PATHS.authCallback} replace /> },
      { path: PATHS.authCallback, element: <AuthCallbackPage /> },
      // "Começar grátis" na landing — força a tela de signup do Auth0
      { path: PATHS.signup, element: <SignupRedirect /> },
      // As três rotas abaixo lidam com o Auth0 sozinhas: o ProtectedRoute
      // redireciona não autenticados para "/" e perderia o token ou o ?plano=.
      // Link de pagamento gerado por /admin/billing/convites.
      { path: `${PATHS.checkout}/:token`, lazy: billing("PaymentInvitePage") },
      // Convite de acesso: o médico entra numa clínica que já paga, sem checkout.
      { path: `${PATHS.accessInvite}/:token`, lazy: billing("AccessInvitePage") },
      // "Assinar" na landing.
      { path: PATHS.checkout, lazy: billing("CheckoutRedirect") },
      {
        element: <ProtectedRoute />,
        children: [
          // Retorno do Stripe Checkout
          { path: PATHS.checkoutConfirming, lazy: billing("CheckoutConfirmingPage") },
          { path: PATHS.onboarding, element: <OnboardingPage /> },
          { path: PATHS.newOrder, element: <NewOrderPage /> },
          { path: PATHS.procedures, element: <ProceduresPage /> },
          { path: PATHS.opme, element: <OpmePage /> },
          { path: PATHS.surgicalIndication, element: <SurgicalIndicationPage /> },
          { path: PATHS.orderReview, element: <DownloadPage /> },
          { path: PATHS.patientData, element: <PatientDataPage /> },
          { path: PATHS.savedOrders, element: <SavedOrdersPage /> },
          { path: PATHS.appeals, element: <AppealsPage /> },
          { path: PATHS.settings, element: <SettingsPage /> },
          { path: PATHS.plans, lazy: billing("PlansPage") },
          {
            // Seções filhas (/admin/medicos, /admin/clinicas-assinantes…) vêm de adminSections.tsx.
            path: PATHS.admin,
            element: (
              <AdminRoute>
                <Suspense fallback={<LoadingScreen />}>
                  <AdminLayout />
                </Suspense>
              </AdminRoute>
            ),
            children: adminChildRoutes,
          },
        ],
      },
      ...Object.entries(RENAMED_PATHS).map(([from, to]) => ({ path: from, element: <RenamedRedirect to={to} /> })),
      // Galeria de componentes: só em `yarn start`; o build de produção nem inclui o arquivo.
      ...(process.env.NODE_ENV === "development"
        ? [
            {
              path: "/dev/ui",
              lazy: () =>
                import("./DevUiPage").then((m) => ({
                  Component: m.DevUiPage,
                })),
            },
          ]
        : []),
      // Catch-all: unknown paths redirect to the main app
      { path: "*", element: <Navigate to={PATHS.newOrder} replace /> },
    ],
  },
], {
  // Comportamentos da v7 já ligados: a atualização futura não muda nada e os avisos somem.
  future: {
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});
